import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { ButtonBase, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import { CompanyAbout, ContactAbout, ContactDetails, DrawerTabs, Qualifications } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import getLocalizedText from 'features/co-pilot/translationFileProduct';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Assessment } from 'types/analysis-result.types';
import { ProfileValues } from 'types/profile.types';
import AboutCompany from './about-company';
import AboutContact from './about-contact';
import AssessmentOverview from './assessment-overview';
import ContactCard from './contact-card';
import CVGenerationDialog from './cv-generation-dialog';

interface TabProps {
  label: string;
  value: DrawerTabs;
  active: boolean;

  handleActiveTabChange(value: DrawerTabs): () => void;
}

const Tab: React.FC<TabProps> = ({ label, value, active, handleActiveTabChange }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  return (
    <ButtonBase className={cx(classes.tab, active ? 'active' : null)} onClick={handleActiveTabChange(value)} disableRipple>
      <Typography className={classes.tabText}>{t(label)}</Typography>
    </ButtonBase>
  );
};

interface ToggleProps {
  isActive: boolean;

  handleSingleQualifiedContactIsActiveChange?(): void;
}

const AddToPipelineToggle: React.FC<ToggleProps> = ({ isActive, handleSingleQualifiedContactIsActiveChange }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  return (
    <Stack className={classes.toggle} direction="row">
      <ButtonBase className={cx(classes.option, 'exclude', !isActive ? 'active' : undefined)} onClick={handleSingleQualifiedContactIsActiveChange}>
        {t('excludeFromContactList')}
      </ButtonBase>
      <ButtonBase className={cx(classes.option, 'add', isActive ? 'active' : undefined)} onClick={handleSingleQualifiedContactIsActiveChange}>
        {t('addToContactList')}
      </ButtonBase>
    </Stack>
  );
};

interface Props {
  open: boolean;
  productGroup: ProductGroups;
  funnelId: string | undefined;

  contactIndex?: number | null;
  profileId: string | undefined;

  aiJudgement: string | undefined;
  aiJudgementExplanation: string | undefined;
  assessment: Assessment | undefined;
  percentiles: ProfileValues | undefined;
  isActive: boolean | undefined;

  contactDetails: ContactDetails | null;
  aboutContact: ContactAbout | null;
  aboutCompany: CompanyAbout | null;

  contactList?: FunnelContact[] | undefined;
  isActiveToggleDisabled?: boolean;

  handleGetNextContact?(): void;

  handleGetPrevContact?(): void;

  handleUpdateDrawerContactIsActive(newIsActive: boolean): () => void;

  handleCloseDrawer(): void;
}

const ContactDrawer: React.FC<Props> = ({
  open,
  productGroup,
  funnelId,
  contactIndex,
  profileId,
  aiJudgement,
  aiJudgementExplanation,
  assessment,
  percentiles,
  isActive,
  contactDetails,
  aboutContact,
  aboutCompany,
  contactList,
  isActiveToggleDisabled,
  handleGetNextContact,
  handleGetPrevContact,
  handleUpdateDrawerContactIsActive,
  handleCloseDrawer,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [activeContactsTab, setActiveContactsTab] = useState<DrawerTabs>(DrawerTabs.ASSESSMENT_OVERVIEW);
  const fullName = contactDetails?.fullName;

  const [cvModalOpen, setCvModalOpen] = useState<boolean>(false);

  const contactListLength = contactList?.length;

  const handleActiveContactsTabChange = (value: DrawerTabs) => () => {
    setActiveContactsTab(value);
  };

  const qualificationToText = () => {
    switch (aiJudgement as Qualifications) {
      case Qualifications.A:
        return getLocalizedText('topCandidate', productGroup, t);
      case Qualifications.B:
        return getLocalizedText('strongContender', productGroup, t);
      case Qualifications.C:
        return getLocalizedText('backupCandidate', productGroup, t);
      default:
        return '';
    }
  };

  const getTabContent = () => {
    switch (activeContactsTab) {
      case DrawerTabs.ASSESSMENT_OVERVIEW:
        return (
          <AssessmentOverview
            aiJudgementExplanation={aiJudgementExplanation}
            fullName={fullName}
            assessment={assessment}
            percentiles={percentiles}
            qualificationToText={qualificationToText}
          />
        );
      case DrawerTabs.ABOUT_CONTACT:
        return <AboutContact contactDetails={contactDetails} aboutContact={aboutContact} qualificationToText={qualificationToText} />;
      case DrawerTabs.ABOUT_COMPANY:
        return <AboutCompany aboutCompany={aboutCompany} />;
      default:
        return <></>;
    }
  };

  const isNextContactDisabled = () => {
    if (_.isNil(contactList) || _.isNil(contactIndex)) return true;
    const nextContactIndex = contactIndex === contactList.length - 1 ? 0 : contactIndex + 1;
    const nextContact = contactList[nextContactIndex];
    return _.isNil(nextContact) || !nextContact.preprocessedProfileAnalysis;
  };

  const isPrevContactDisabled = () => {
    if (_.isNil(contactList) || _.isNil(contactIndex)) return true;
    const prevContactIndex = contactIndex === 0 ? contactList.length - 1 : contactIndex - 1;
    const prevContact = contactList[prevContactIndex];
    return _.isNil(prevContact) || !prevContact.preprocessedProfileAnalysis;
  };

  /**
   * CV Generation dialog
   */

  const handleExportContact = () => {
    setCvModalOpen(true);
  };

  const handleCloseCvModal = () => {
    setCvModalOpen(false);
  };

  return (
    <>
      <Drawer classes={{ paper: classes.drawer, modal: classes.modal }} anchor="right" open={open}>
        <Stack className={classes.drawerHeader} gap={1.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" gap={2}>
              {!_.isNil(handleGetPrevContact) ? (
                <IconButton className={classes.iconButton} disabled={isPrevContactDisabled()} onClick={handleGetPrevContact}>
                  <ArrowBackIcon />
                </IconButton>
              ) : null}
              <Typography className={classes.headerText} fontWeight="bold">
                {getLocalizedText('talentPreview', productGroup, t)}
              </Typography>
              {!_.isNil(contactIndex) && !_.isNil(contactListLength) ? (
                <Typography className={classes.headerText}>{`(${(contactIndex ?? 0) + 1} / ${contactListLength})`}</Typography>
              ) : null}
              {!_.isNil(handleGetNextContact) ? (
                <IconButton className={classes.iconButton} disabled={isNextContactDisabled()} onClick={handleGetNextContact}>
                  <ArrowForwardIcon />
                </IconButton>
              ) : null}
            </Stack>
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <Tooltip title={getLocalizedText('exportContact', productGroup, t)}>
                <IconButton className={classes.iconButton} onClick={handleExportContact}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <IconButton className={classes.closeButton} onClick={handleCloseDrawer}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
          {!isActiveToggleDisabled && (
            <AddToPipelineToggle isActive={!!isActive} handleSingleQualifiedContactIsActiveChange={handleUpdateDrawerContactIsActive(!isActive)} />
          )}
        </Stack>
        <Stack className={classes.drawerContent}>
          <ContactCard contactDetails={contactDetails} isActive={!!isActive} qualification={aiJudgement} qualificationToText={qualificationToText} />
          <div className={classes.tabsContainer}>
            <Stack className={classes.tabs} direction="row" alignItems="flex-start" gap={4}>
              {_.map(DrawerTabs, (tab) => (
                <Tab key={tab} label={tab} value={tab} active={activeContactsTab === tab} handleActiveTabChange={handleActiveContactsTabChange} />
              ))}
            </Stack>
            <Stack className={classes.tabContent} gap={2}>
              {getTabContent()}
            </Stack>
          </div>
        </Stack>
      </Drawer>

      {/* CV Generation Modal */}
      <CVGenerationDialog
        open={cvModalOpen}
        onClose={handleCloseCvModal}
        profileId={profileId}
        contactName={contactDetails?.fullName}
        funnelId={funnelId}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  modal: {
    zIndex: 1300,
  },
  drawer: {
    width: '700px',
    height: '100%',
  },
  drawerHeader: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    padding: '12px 20px 48px 20px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
  },
  headerText: {
    fontSize: theme.typography.pxToRem(20),
    color: '#FFF',
  },
  iconButton: {
    color: '#FFF',
    padding: '2px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(24),
    },
  },
  closeButton: {
    color: '#FFF',
    padding: '2px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(24),
    },
  },
  toggle: {
    width: 'fit-content',
    borderRadius: '16px',
    border: `1px solid ${theme.palette.greys.light}`,
    overflow: 'hidden',
    display: 'inline-flex',
  },
  option: {
    color: '#FFF',
    padding: '6px 16px',
    fontSize: theme.typography.pxToRem(13),
    whiteSpace: 'nowrap',
    '&:not(:last-child)': {
      borderRight: `1px solid ${theme.palette.greys.light}`,
    },
    '&:hover': {
      background: theme.palette.action.hover,
    },
    '&.active': {
      color: '#FFF',
      pointerEvents: 'none',
      '&.exclude': {
        background: theme.palette.backgrounds.inactive,
        color: theme.palette.primary.main,
      },
      '&.add': {
        background: theme.palette.backgrounds.active,
        color: theme.palette.text.primary,
      },
    },
  },
  drawerContent: {
    padding: '0 20px 8px 20px',
    marginTop: '-32px',
    overflow: 'hidden',
  },
  tabsContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    marginLeft: '-20px',
    marginRight: '-20px',
    marginTop: '20px',
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.greys.light}`,
    paddingLeft: '20px',
    paddingRight: '20px',
  },
  tab: {
    padding: '8px 0',
    color: theme.palette.text.primary,
    '&:hover': {
      borderBottom: `3px solid ${theme.palette.primary.main}`,
      paddingBottom: '6px',
    },
    '&.active': {
      color: theme.palette.primary.main,
      borderBottom: `3px solid ${theme.palette.primary.main}`,
      paddingBottom: '6px',
    },
  },
  tabText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
  },
  tabContent: {
    padding: '20px 20px 1px 20px',
    overflow: 'auto',
  },
  drawerFooter: {
    padding: '12px',
    borderTop: `1px solid ${theme.palette.greys.light}`,
  },
}));

export default ContactDrawer;
